import React, { useState } from 'react';
import { X, Search, Building2, User, Phone } from 'lucide-react';
import { usePartners } from '../context/PartnerContext';

function SelectCompanyModal({ isOpen, onClose, onSelect }) {
  const { partners } = usePartners();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCompany, setSelectedCompany] = useState(null);

  const filteredCompanies = partners.filter(company =>
    company.companyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    company.gstin?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleContinue = () => {
    if (selectedCompany) {
      onSelect({
        ...selectedCompany,
        gstNumber: selectedCompany.gstin // Map gstin to gstNumber for AddPurchaseOrder
      });
      setSelectedCompany(null);
      setSearchTerm('');
    }
  };

  const handleClose = () => {
    setSelectedCompany(null);
    setSearchTerm('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] transition-all">
      <div className="bg-white rounded-[2rem] w-full max-w-2xl mx-4 max-h-[85vh] overflow-hidden flex flex-col shadow-2xl border border-white/20">
        {/* Header */}
        <div className="flex items-center justify-between px-8 py-6 border-b border-[#E1F2ED] bg-[#F4FBF9]">
          <div className="flex items-center space-x-3">
            <Building2 className="w-6 h-6 text-[#4B9C86]" />
            <h2 className="text-[20px] font-black text-[#1E4D43] uppercase tracking-wide">Select Partner</h2>
          </div>
          <button
            onClick={handleClose}
            className="w-10 h-10 flex items-center justify-center hover:bg-[#E1F2ED] rounded-full transition-all text-[#4B9C86]"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Search */}
        <div className="p-8 pb-4">
          <div className="relative group">
            <Search className="absolute left-5 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#4B9C86] group-focus-within:scale-110 transition-transform" />
            <input
              type="text"
              placeholder="Search by company name or GST number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-14 pr-6 py-4 bg-[#F2FBF9] border-2 border-[#E1F2ED] rounded-2xl focus:outline-none focus:border-[#4B9C86] text-[#1E4D43] font-bold placeholder:text-gray-400 transition-all shadow-inner"
            />
          </div>
        </div>

        {/* Company List */}
        <div className="flex-1 overflow-y-auto px-8 py-4 space-y-4 custom-scrollbar">
          {filteredCompanies.length > 0 ? (
            filteredCompanies.map((company) => (
              <div
                key={company.id}
                onClick={() => setSelectedCompany(company)}
                className={`relative p-5 rounded-2xl cursor-pointer border-2 transition-all duration-200 group ${selectedCompany?.id === company.id
                    ? 'bg-[#EBF5F1] border-[#4B9C86] shadow-md shadow-[#4B9C86]/5'
                    : 'bg-white border-[#E1F2ED] hover:border-[#4B9C86] hover:bg-[#F9FBFA] hover:shadow-sm'
                  }`}
              >
                <div className="flex items-start justify-between">
                  <div className="space-y-3 flex-1">
                    <div className="flex items-center space-x-3">
                      <div className={`p-2 rounded-lg ${selectedCompany?.id === company.id ? 'bg-[#4B9C86] text-white' : 'bg-[#F2FBF9] text-[#4B9C86]'}`}>
                        <Building2 size={18} />
                      </div>
                      <span className="text-[18px] font-black text-[#1E4D43]">{company.companyName}</span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-y-2 gap-x-6 ml-11">
                      <div className="flex items-center space-x-2 text-gray-500 font-bold text-xs uppercase">
                        <span className="text-[#4B9C86]">GST:</span>
                        <span className="text-[#1E4D43] opacity-80">{company.gstin}</span>
                      </div>
                      <div className="flex items-center space-x-2 text-gray-500 font-bold text-xs uppercase">
                        <User size={12} className="text-[#4B9C86]" />
                        <span className="text-[#1E4D43] opacity-80">{company.contactPerson}</span>
                      </div>
                      <div className="flex items-center space-x-2 text-gray-500 font-bold text-xs uppercase">
                        <Phone size={12} className="text-[#4B9C86]" />
                        <span className="text-[#1E4D43] opacity-80">{company.phone}</span>
                      </div>
                    </div>
                  </div>

                  <div className={`w-6 h-6 border-2 rounded-full flex items-center justify-center transition-all ${selectedCompany?.id === company.id
                      ? 'border-[#4B9C86] bg-[#4B9C86]'
                      : 'border-[#BDDBCE] bg-white group-hover:border-[#4B9C86]'
                    }`}>
                    {selectedCompany?.id === company.id && <div className="w-2.5 h-2.5 bg-white rounded-full" />}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="w-20 h-20 bg-[#F2FBF9] rounded-full flex items-center justify-center text-[#4B9C86] mb-4">
                <Search size={32} />
              </div>
              <p className="text-[18px] font-bold text-[#1E4D43]">No partners found</p>
              <p className="text-gray-400 text-sm mt-1 mb-6">Try searching with a different name or GST number</p>
              <button
                onClick={handleClose}
                className="px-6 py-2.5 bg-[#4B9C86] text-white rounded-xl font-bold hover:bg-[#3D826F] transition-all"
              >
                Go Back
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-8 border-t border-[#E1F2ED] bg-[#F9FBFA] flex items-center justify-between">
          <p className="text-[12px] font-bold text-[#1E4D43] opacity-50 uppercase">
            {filteredCompanies.length} Partners Available
          </p>
          <div className="flex space-x-4">
            <button
              type="button"
              onClick={handleClose}
              className="px-6 py-3 text-[#1E4D43] font-black uppercase tracking-wider text-sm hover:underline"
            >
              Cancel
            </button>
            <button
              onClick={handleContinue}
              disabled={!selectedCompany}
              className={`px-10 py-3 rounded-2xl font-black uppercase tracking-wider text-sm transition-all shadow-md active:scale-[0.98] ${!selectedCompany
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-[#BDDBCE] text-[#1E4D43] hover:bg-[#A9CCBD]'
                }`}
            >
              Select & Continue
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SelectCompanyModal;